import { Button, Field, Loading, Seg, useToast, ErrorNote } from "@corecrm/ui";
import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
  useInspectSetPassword,
  useMe,
  useRequestPasswordReset,
  useRequestSignInLink,
  useSetPassword,
  useSignIn,
  useVerifySignInLink,
} from "../api/queries";
import { PublicShell } from "../components/bits";

/** Where to go after signing in: the page that sent them here, if it's one of ours. */
export function safeNext(next: string | null): string {
  return next && next.startsWith("/") && !next.startsWith("//") ? next : "/";
}

export function SignInPage() {
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const me = useMe();
  const navigate = useNavigate();
  const signIn = useSignIn();
  const sendLink = useRequestSignInLink();
  const [mode, setMode] = useState<"password" | "link">("password");
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [password, setPassword] = useState("");

  if (me.data) return <Navigate to={next} replace />;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (mode === "password")
      signIn.mutate({ email, password }, { onSuccess: () => navigate(next, { replace: true }) });
    else sendLink.mutate(next === "/" ? email : { email, next });
  };

  if (sendLink.isSuccess)
    return (
      <PublicShell title="Check your email">
        <h1>Check your email</h1>
        <p className="muted">
          If <strong>{email}</strong> has a client portal login, we've sent it a sign-in link. The link works
          once and expires in an hour.
        </p>
        <div className="row">
          <Button onClick={() => sendLink.reset()}>Back to sign in</Button>
        </div>
      </PublicShell>
    );

  const error = signIn.error ?? sendLink.error;
  return (
    <PublicShell title="Sign in">
      <h1>Sign in</h1>
      <p className="muted">
        {next.startsWith("/quotes/")
          ? "Sign in to see your quote. You'll go straight to it afterwards."
          : next.startsWith("/invoices/")
            ? "Sign in to see your invoice. You'll go straight to it afterwards."
            : "See your quotes, projects, approvals and invoices from CoreNode."}
      </p>
      <form className="stack" onSubmit={submit} noValidate>
        <Seg
          label="How to sign in"
          value={mode}
          onChange={setMode}
          options={[
            ["password", "Password"],
            ["link", "Email me a link"],
          ]}
        />
        <Field label="Email">
          <input
            type="email"
            autoComplete="email"
            inputMode="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        {mode === "password" && (
          <Field label="Password" hint={<Link to="/forgot-password">Forgot your password?</Link>}>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
        )}
        {error && <ErrorNote error={error} />}
        <Button
          type="submit"
          variant="primary"
          busy={signIn.isPending || sendLink.isPending}
          disabled={!email || (mode === "password" && !password)}
        >
          {mode === "password" ? "Sign in" : "Email me a sign-in link"}
        </Button>
      </form>
      <p className="faint">
        New here? Sign up with the link in the email CoreNode sent you: it sets up your login and brings you
        to your quote. No email? Ask your CoreNode contact to send you a portal invite.
      </p>
    </PublicShell>
  );
}

/** /sign-in/link?token=… from a one-time sign-in email. */
export function SignInLinkPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const verify = useVerifySignInLink();
  const started = useRef(false);

  useEffect(() => {
    if (started.current || !token) return; // once, even under StrictMode's double effect
    started.current = true;
    verify.mutate(token, {
      onSuccess: () => navigate(safeNext(params.get("next")), { replace: true }),
    });
  }, [token, verify, navigate, params]);

  if (!token || verify.isError)
    return (
      <PublicShell title="Sign-in link">
        <h1>This sign-in link isn't valid</h1>
        <p className="muted">
          Sign-in links work once and expire after an hour. Request a fresh one and use the newest email.
        </p>
        <div className="row">
          <Link className="btn primary" to="/sign-in">
            Get a new link
          </Link>
        </div>
      </PublicShell>
    );
  return (
    <PublicShell title="Signing you in">
      <Loading label="Signing you in…" />
    </PublicShell>
  );
}

export function ForgotPasswordPage() {
  const reset = useRequestPasswordReset();
  const me = useMe();
  const [email, setEmail] = useState("");
  useEffect(() => {
    if (me.data && !email) setEmail(me.data.email);
  }, [me.data, email]);

  if (reset.isSuccess)
    return (
      <PublicShell title="Check your email">
        <h1>Check your email</h1>
        <p className="muted">
          If <strong>{email}</strong> has a client portal login, we've sent it a link to choose a new
          password. It expires in 72 hours.
        </p>
        <div className="row">
          <Link className="btn" to="/sign-in">
            Back to sign in
          </Link>
        </div>
      </PublicShell>
    );
  return (
    <PublicShell title="Reset your password">
      <h1>Choose a new password</h1>
      <p className="muted">We'll email you a link to set a new password.</p>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          reset.mutate(email);
        }}
        noValidate
      >
        <Field label="Email">
          <input
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        {reset.error && <ErrorNote error={reset.error} />}
        <div className="row">
          <Button type="submit" variant="primary" busy={reset.isPending} disabled={!email}>
            Email me a link
          </Button>
          <Link className="btn ghost" to="/sign-in">
            Back to sign in
          </Link>
        </div>
      </form>
    </PublicShell>
  );
}

export const MIN_PASSWORD = 12;

/** /set-password?token=… from a reset email. */
export function SetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const details = useInspectSetPassword(token);
  const setPassword = useSetPassword();
  const navigate = useNavigate();
  const toast = useToast();
  const [password, setPw] = useState("");
  const short = password.length > 0 && password.length < MIN_PASSWORD;

  if (!token || details.isError)
    return (
      <PublicShell title="Password link">
        <h1>This link isn't valid</h1>
        <p className="muted">Password links work once and expire after 72 hours. Ask for a new one.</p>
        <div className="row">
          <Link className="btn primary" to="/forgot-password">
            Get a new link
          </Link>
        </div>
      </PublicShell>
    );
  if (details.isPending)
    return (
      <PublicShell title="Choose a password">
        <Loading />
      </PublicShell>
    );
  const email = details.data.email;
  return (
    <PublicShell title="Choose a password">
      <h1>Choose a password</h1>
      <p className="muted">
        For <strong>{email}</strong>. Use at least {MIN_PASSWORD} characters.
      </p>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          setPassword.mutate(
            { token, password },
            {
              onSuccess: () => {
                toast("Password saved. Sign in with it now.");
                navigate(`/sign-in?email=${encodeURIComponent(email)}`, { replace: true });
              },
            },
          );
        }}
        noValidate
      >
        <input type="email" hidden readOnly autoComplete="username" value={email} />
        <Field label="New password" error={short ? `At least ${MIN_PASSWORD} characters.` : null}>
          <input
            type="password"
            autoComplete="new-password"
            minLength={MIN_PASSWORD}
            value={password}
            onChange={(e) => setPw(e.target.value)}
          />
        </Field>
        {setPassword.error && <ErrorNote error={setPassword.error} />}
        <Button
          type="submit"
          variant="primary"
          busy={setPassword.isPending}
          disabled={password.length < MIN_PASSWORD}
        >
          Save password
        </Button>
      </form>
    </PublicShell>
  );
}
